/* eslint-disable default-case */
/* eslint-disable complexity */
import * as THREE from 'three';
import { IBuildingProviderContext } from '../@types/ContextProviderRelated';
import { EWall } from '../@types/Enums';
import { EAssetType } from '../@types/FilesAPIModels';
import { EHistoryAction } from '../hooks/useUndoRedo';
import { importWalls } from '../importer/utils/@v_1_0/importWalls';
import { IWall } from '../models/abstract/Wall';
import AttributeModel from '../models/standard/AttributeModel';
import CameraPositionSpriteModel from '../models/standard/CameraPositionSpriteModel';
import CompassModel from '../models/standard/CompassModel';
import DoorModel from '../models/standard/DoorModel';
import FloorModel from '../models/standard/FloorModel';
import HotspotModel from '../models/standard/HotspotModel';
import PanoMeasurementModel from '../models/standard/PanoMeasurementModel';
import PassageModel from '../models/standard/PassageModel';
import PartitionWallModel from '../models/standard/PartitionWallModel';
import PhysicalBoundaryModel, { BoundaryEdge } from '../models/standard/PhysicalBoundary';
import PlanModel from '../models/standard/PlanModel';
import RadiatorModel from '../models/standard/RadiatorModel';
import RoomModel from '../models/standard/RoomModel';
import SimpleMeasurementModel from '../models/standard/SimpleMeasurementModel';
import SpaceModel from '../models/standard/SpaceModel';
import StandardWallModel from '../models/standard/StandardWallModel';
import WindowModel from '../models/standard/WindowModel';
import { assignAttributesToImportedPanoMeasurements } from './attributes/attributesForObjectLoader';
import { DefaultAttributeId } from './attributes/defaultObjectsAndAttributes';
import { DEFAULT_WALL_DEPTH } from './constants';
import { getBoundingBox } from './exportUtils';
import { importPlanJsonV1 } from './import/importPlanJSONV1';
import { importPlanJsonV2 } from './import/importPlanJSONV2';
import { importPlanJsonV3 } from './import/importPlanJSONV3';

export interface NonFetchableEntity {
  objectType: 'notFetchableEntity';
  modelType: 'floor' | 'product' | 'element' | 'model2D' | 'unknown';
  type: 'texture' | 'assets' | 'modelData' | 'unknown';
  subType: EAssetType | undefined;
  id: string;
  entityData: any;
}

type ImportedWallsResult = {
  walls: Array<IWall>;
  roomAttributes?: Array<any>;
  floorAttributes?: Array<any>;
  cameraOffset?: THREE.Vector3;
  boundaryPoints?: THREE.Vector3[];
};

const EPS = 1e-6;

type Line2D = {
  p: THREE.Vector3;
  dir: THREE.Vector3;
};

const calculateSignedArea = (vertices: THREE.Vector3[]): number => {
  let area = 0;
  for (let i = 0; i < vertices.length; i++) {
    const j = (i + 1) % vertices.length;
    area += vertices[i].x * vertices[j].y - vertices[j].x * vertices[i].y;
  }
  return area / 2;
};

const ensureClockwise = (points: THREE.Vector3[]): THREE.Vector3[] => {
  const cloned = points.map((p) => p.clone());
  if (calculateSignedArea(cloned) > 0) {
    cloned.reverse();
  }
  return cloned;
};

const calculateCentroid = (vertices: THREE.Vector3[]): THREE.Vector3 => {
  let sumX = 0;
  let sumY = 0;
  vertices.forEach((v) => {
    sumX += v.x;
    sumY += v.y;
  });
  return new THREE.Vector3(sumX / vertices.length, sumY / vertices.length, 0);
};

const rotatePoint = (point: THREE.Vector3, angle: number): void => {
  const cosA = Math.cos(angle);
  const sinA = Math.sin(angle);
  const { x, y } = point;
  point.x = x * cosA - y * sinA;
  point.y = x * sinA + y * cosA;
};

const intersectLines2D = (a: Line2D, b: Line2D): THREE.Vector3 | null => {
  const cross = a.dir.x * b.dir.y - a.dir.y * b.dir.x;
  if (Math.abs(cross) < EPS) return null;

  const t = ((b.p.x - a.p.x) * b.dir.y - (b.p.y - a.p.y) * b.dir.x) / cross;
  return new THREE.Vector3(a.p.x + t * a.dir.x, a.p.y + t * a.dir.y, 0);
};

/**
 * Inner polygon -> Center-line polygon.
 * Überarbeiteter Fix: Arrays werden nicht umgedreht, Indizes bleiben synchron!
 */
const innerPolygonToCenterLine = (innerPoints: THREE.Vector3[], wallDepth: number): THREE.Vector3[] => {
  const cleanPoints = [...innerPoints];
  
  if (cleanPoints.length > 1 && cleanPoints[0].distanceTo(cleanPoints[cleanPoints.length - 1]) < EPS) {
    cleanPoints.pop();
  }

  if (cleanPoints.length < 3) return cleanPoints.map((p) => p.clone());

  const area = calculateSignedArea(cleanPoints);
  const isCW = area <= 0;

  const halfDepth = wallDepth / 2;

  const offsetLines: Line2D[] = cleanPoints.map((p, i) => {
    const next = cleanPoints[(i + 1) % cleanPoints.length];
    const dir = new THREE.Vector3().subVectors(next, p).normalize();

    const outwardNormal = isCW 
      ? new THREE.Vector3(-dir.y, dir.x, 0) 
      : new THREE.Vector3(dir.y, -dir.x, 0);

    return {
      p: p.clone().add(outwardNormal.multiplyScalar(halfDepth)),
      dir,
    };
  });

  const centerLinePoints: THREE.Vector3[] = [];
  for (let i = 0; i < cleanPoints.length; i++) {
    const prev = offsetLines[(i - 1 + cleanPoints.length) % cleanPoints.length];
    const curr = offsetLines[i];
    const hit = intersectLines2D(prev, curr);
    centerLinePoints.push(hit ?? curr.p.clone());
  }

  return centerLinePoints;
};

type CreateWallsFromMeasurementsResult = {
  walls: StandardWallModel[];
  boundaryPoints: THREE.Vector3[];
  cameraOffset: THREE.Vector3;
  alignmentRotationDegrees: number;
};

type SingleImportAlignmentResult = {
  walls: any[];
  boundaryPoints: THREE.Vector3[];
  cameraOffset: THREE.Vector3;
  alignmentRotationDegrees: number;
  compassOffsetDegrees: number;
};

const getVersionedCompassValue = (pano: PanoMeasurementModel, sceneExport: any): number | undefined => {
  const candidates = [sceneExport?.compass, sceneExport?.measurements?.compass, pano?.compass];

  for (const candidate of candidates) {
    const value = typeof candidate === 'number' ? candidate : Number(candidate);
    if (Number.isFinite(value)) return value;
  }

  return undefined;
};

const transformWallItems = (wall: any, transformPoint: (point: THREE.Vector3) => THREE.Vector3, rotationRadians: number): void => {
  if (Array.isArray(wall.windows)) {
    wall.windows.forEach((window: any) => {
      if (window.position) window.position = transformPoint(window.position);
      if (typeof window.angle === 'number') window.angle += rotationRadians;
    });
  }

  if (Array.isArray(wall.doors)) {
    wall.doors.forEach((door: any) => {
      if (door.position) door.position = transformPoint(door.position);
      if (typeof door.angle === 'number') door.angle += rotationRadians;
    });
  }

  if (typeof wall.getAllRadiators === 'function') {
    wall.getAllRadiators().forEach((radiator: any) => {
      if (radiator.position) radiator.position = transformPoint(radiator.position);
      if (typeof radiator.angle === 'number') radiator.angle += rotationRadians;
    });
  }
};

const alignImportedRoomToLongestWall = (
  walls: any[],
  boundaryPoints: THREE.Vector3[],
  cameraOffset: THREE.Vector3,
): SingleImportAlignmentResult => {
  const sourcePoints = ensureClockwise(boundaryPoints.length >= 3 ? boundaryPoints : walls.flatMap((w) => [w.startPoint, w.endPoint]).filter(Boolean));

  if (sourcePoints.length < 3) {
    return { walls, boundaryPoints, cameraOffset, alignmentRotationDegrees: 0, compassOffsetDegrees: 0 };
  }

  let longestIndex = 0;
  let longestDist = 0;

  for (let i = 0; i < sourcePoints.length; i++) {
    const nextI = (i + 1) % sourcePoints.length;
    const dist = sourcePoints[i].distanceTo(sourcePoints[nextI]);
    if (dist > longestDist) {
      longestDist = dist;
      longestIndex = i;
    }
  }

  const longestStart = sourcePoints[longestIndex].clone();
  const longestEnd = sourcePoints[(longestIndex + 1) % sourcePoints.length].clone();
  const sourceCentroid = calculateCentroid(sourcePoints);
  const sourceSideMidpoint = new THREE.Vector3((longestStart.x + longestEnd.x) / 2, (longestStart.y + longestEnd.y) / 2, 0);
  const shouldUseReversedLongestSide = sourceSideMidpoint.y < sourceCentroid.y;
  const sideStart = shouldUseReversedLongestSide ? longestEnd.clone() : longestStart.clone();
  const sideEnd = shouldUseReversedLongestSide ? longestStart.clone() : longestEnd.clone();
  const sideAngle = Math.atan2(sideEnd.y - sideStart.y, sideEnd.x - sideStart.x);
  const alignmentRotationRadians = -sideAngle;
  const alignmentRotationDegrees = (alignmentRotationRadians * 180) / Math.PI;
  const rotateAroundSideStart = (point: THREE.Vector3): THREE.Vector3 => {
    const rotated = point.clone();
    rotated.x -= sideStart.x;
    rotated.y -= sideStart.y;
    rotatePoint(rotated, alignmentRotationRadians);
    rotated.x += sideStart.x;
    rotated.y += sideStart.y;
    return rotated;
  };

  let transformedBoundaryPoints = sourcePoints.map(rotateAroundSideStart);
  let transformedCameraOffset = rotateAroundSideStart(cameraOffset);

  const transformedWalls = walls.map((wall) => {
    const transformedWall = wall;
    transformedWall.startPoint = rotateAroundSideStart(wall.startPoint);
    transformedWall.endPoint = rotateAroundSideStart(wall.endPoint);
    transformWallItems(transformedWall, rotateAroundSideStart, alignmentRotationRadians);
    if (typeof transformedWall.resetPoints === 'function') transformedWall.resetPoints();
    return transformedWall;
  });

  const transformedSideStart = rotateAroundSideStart(sideStart);
  const transformedSideEnd = rotateAroundSideStart(sideEnd);
  const transformedSideMidpoint = new THREE.Vector3(
    (transformedSideStart.x + transformedSideEnd.x) / 2,
    (transformedSideStart.y + transformedSideEnd.y) / 2,
    0,
  );
  const transformedCentroid = calculateCentroid(transformedBoundaryPoints);
  const finalAlignmentRotationDegrees = alignmentRotationDegrees;

  if (transformedSideMidpoint.y > transformedCentroid.y) {
    const rotateByPiAroundCentroid = (point: THREE.Vector3): THREE.Vector3 => {
      const rotated = point.clone();
      rotated.x -= transformedCentroid.x;
      rotated.y -= transformedCentroid.y;
      rotatePoint(rotated, Math.PI);
      rotated.x += transformedCentroid.x;
      rotated.y += transformedCentroid.y;
      return rotated;
    };

    transformedBoundaryPoints = transformedBoundaryPoints.map(rotateByPiAroundCentroid);
    transformedCameraOffset = rotateByPiAroundCentroid(transformedCameraOffset);

    transformedWalls.forEach((wall) => {
      wall.startPoint = rotateByPiAroundCentroid(wall.startPoint);
      wall.endPoint = rotateByPiAroundCentroid(wall.endPoint);
      transformWallItems(wall, rotateByPiAroundCentroid, Math.PI);
      if (typeof wall.resetPoints === 'function') wall.resetPoints();
    });
  }

  return {
    walls: transformedWalls,
    boundaryPoints: transformedBoundaryPoints,
    cameraOffset: transformedCameraOffset,
    alignmentRotationDegrees: finalAlignmentRotationDegrees,
    compassOffsetDegrees: 0,
  };
};

export const importPlanJson = async (
  jsonString: string,
  isFilesApiAvailable?: boolean,
): Promise<{
  plan: PlanModel;
  building: IBuildingProviderContext;
  measurements: SimpleMeasurementModel[];
  cameraPositionSprites: CameraPositionSpriteModel[];
  hotspots: HotspotModel[];
  nonFetchableEntities: Array<NonFetchableEntity>;
}> => {
  const data = JSON.parse(jsonString);
  const version = data.plan.jsonVersion;

  switch (version) {
    case '1.0':
    case '1.1':
    case '1.2':
      return importPlanJsonV1(jsonString, isFilesApiAvailable);
    case '2.0':
      return importPlanJsonV2(jsonString);
    case '3.0':
      return importPlanJsonV3(jsonString);
    default:
      return importPlanJsonV1(jsonString, isFilesApiAvailable);
  }
};

export const createWallsFromMeasurements = (
  panoMeasurementModel: PanoMeasurementModel,
): CreateWallsFromMeasurementsResult => {
  const walls: StandardWallModel[] = [];
  const cameraOffset = new THREE.Vector3(0, 0, 0);
  let boundaryPoints: THREE.Vector3[] = [];
  let alignmentRotationDegrees = 0;

  let factorCameraHeight = 1624;
  if (panoMeasurementModel.cameraHeight && panoMeasurementModel.cameraHeight !== 0) {
    factorCameraHeight = panoMeasurementModel.cameraHeight;
  } else if (panoMeasurementModel.defaultCameraStandHeight && panoMeasurementModel.defaultCameraStandHeight !== 0) {
    factorCameraHeight = panoMeasurementModel.defaultCameraStandHeight;
  }
  factorCameraHeight = factorCameraHeight / 100;

  panoMeasurementModel.measurements.forEach((measurement) => {
    if (!Array.isArray(measurement) || measurement.length < 6) return;

    const vertices: THREE.Vector3[] = [];
    for (let i = 0; i < measurement.length; i += 3) {
      const x = measurement[i] * factorCameraHeight;
      const y = measurement[i + 2] * factorCameraHeight;
      vertices.push(new THREE.Vector3(x, y, 0));
    }

    if (vertices.length > 1 && vertices[0].equals(vertices[vertices.length - 1])) {
      vertices.pop();
    }

    if (vertices.length < 3) return;

    const area = calculateSignedArea(vertices);
    if (area > 0) {
      vertices.reverse();
    }

    let longestIndex = 0;
    let longestDist = 0;
    for (let i = 0; i < vertices.length; i++) {
      const nextI = (i + 1) % vertices.length;
      const dist = vertices[i].distanceTo(vertices[nextI]);
      if (dist > longestDist) {
        longestDist = dist;
        longestIndex = i;
      }
    }

    const sideStart = vertices[longestIndex].clone();
    const sideEnd = vertices[(longestIndex + 1) % vertices.length].clone();
    const dx = sideEnd.x - sideStart.x;
    const dy = sideEnd.y - sideStart.y;

    const angle = Math.atan2(dy, dx);
    alignmentRotationDegrees = (-angle * 180) / Math.PI;
    vertices.forEach((v) => {
      v.x -= sideStart.x;
      v.y -= sideStart.y;
      rotatePoint(v, -angle);
      v.x += sideStart.x;
      v.y += sideStart.y;
    });

    const newSideStart = vertices[longestIndex];
    const newSideEnd = vertices[(longestIndex + 1) % vertices.length];
    const midpoint = new THREE.Vector3((newSideStart.x + newSideEnd.x) / 2, (newSideStart.y + newSideEnd.y) / 2, 0);
    const centroid = calculateCentroid(vertices);

    let reflected = false;
    if (midpoint.y > centroid.y) {
      vertices.forEach((v) => {
        v.y = -v.y;
      });
      vertices.reverse();
      reflected = true;
    }

    let minY = Infinity;
    vertices.forEach((v) => {
      if (v.y < minY) minY = v.y;
    });
    vertices.forEach((v) => {
      v.y -= minY;
    });

    cameraOffset.sub(sideStart);
    rotatePoint(cameraOffset, -angle);
    cameraOffset.add(sideStart);
    if (reflected) {
      cameraOffset.y = -cameraOffset.y;
    }
    cameraOffset.y -= minY;
    // 1. Sichere die exakten Innenpunkte für die Boundary
    boundaryPoints = vertices.map((v) => v.clone()); 

    // 2. Stelle den Original-Inline-Offset wieder her, der die Rotation schützt!
    const h = DEFAULT_WALL_DEPTH / 2;
    const nPts = vertices.length;

    const offsetLines = vertices.map((p, i) => {
      const nextP = vertices[(i + 1) % nPts];
      const dir = new THREE.Vector3().subVectors(nextP, p).normalize();
      
      const normal = new THREE.Vector3(-dir.y, dir.x, 0); 
      const offsetP = p.clone().add(normal.multiplyScalar(h));
      
      return { p: offsetP, dir };
    });

    const intersectLines2D = (p1: THREE.Vector3, dir1: THREE.Vector3, p2: THREE.Vector3, dir2: THREE.Vector3): THREE.Vector3 | null => {
      const crossProd = dir1.x * dir2.y - dir1.y * dir2.x;
      if (Math.abs(crossProd) < 1e-6) return null;
      const t = ((p2.x - p1.x) * dir2.y - (p2.y - p1.y) * dir2.x) / crossProd;
      return new THREE.Vector3(p1.x + t * dir1.x, p1.y + t * dir1.y, 0);
    };

    const centerLinePoints: THREE.Vector3[] = [];
    for (let i = 0; i < nPts; i++) {
      const prev = offsetLines[(i - 1 + nPts) % nPts];
      const curr = offsetLines[i];
      const intersection = intersectLines2D(prev.p, prev.dir, curr.p, curr.dir);
      
      centerLinePoints.push(intersection || curr.p.clone());
    }

    // 3. Wände aus den Center-Line Punkten generieren
    for (let i = 0; i < centerLinePoints.length; i++) {
      const s = centerLinePoints[i].clone();
      const e = centerLinePoints[(i + 1) % centerLinePoints.length].clone();
      const wall = new StandardWallModel(s, e, DEFAULT_WALL_DEPTH);
      walls.push(wall);
    }
  });

  return { walls, boundaryPoints, cameraOffset, alignmentRotationDegrees };
};

function getRoomBoundsWithMinFromPoints(points: THREE.Vector3[]) {
  let minX = Infinity;
  let maxX = -Infinity;
  let minY = Infinity;
  let maxY = -Infinity;

  points.forEach((p) => {
    minX = Math.min(minX, p.x);
    maxX = Math.max(maxX, p.x);
    minY = Math.min(minY, p.y);
    maxY = Math.max(maxY, p.y);
  });

  return {
    width: maxX - minX,
    height: maxY - minY,
    minX,
    minY,
  };
}

function getRoomBoundsWithMin(walls: IWall[]) {
  let minX = Infinity;
  let maxX = -Infinity;
  let minY = Infinity;
  let maxY = -Infinity;

  walls.forEach((w) => {
    [w.startPoint, w.endPoint].forEach((p) => {
      minX = Math.min(minX, p.x);
      maxX = Math.max(maxX, p.x);
      minY = Math.min(minY, p.y);
      maxY = Math.max(maxY, p.y);
    });
  });

  return {
    width: maxX - minX,
    height: maxY - minY,
    minX,
    minY,
  };
}

export const importAllMeasurementsToPlanJson = async (
  panoMeasurements: PanoMeasurementModel[],
  region?: string,
  existingBuilding?: IBuildingProviderContext,
): Promise<{
  building: IBuildingProviderContext;
  cameraOffsets: THREE.Vector3[];
}> => {
  let compass = existingBuilding?.compass || new CompassModel();
  let singleImportAlignmentRotationDegrees = 0;
  const count = panoMeasurements.length;
  const calculatedCameraPositions: THREE.Vector3[] = new Array(count);

  const WORLD_UNITS_PER_METER = 10;
  const RIGHT_LIMIT = 90 * WORLD_UNITS_PER_METER;
  const LEFT_LIMIT = -90 * WORLD_UNITS_PER_METER;
  const UI_PADDING = 1;
  const CLEARANCE = 1;
  const PAD = UI_PADDING + CLEARANCE;
  const ROOM_SPACING = 10;
  const SOLO_ROW_THRESHOLD_M = 50;

  const hasExistingRooms = existingBuilding && existingBuilding.rooms && existingBuilding.rooms.length > 0;
  const isSingleImport = panoMeasurements.length === 1 && !hasExistingRooms;

  if (isSingleImport) {
    compass = new CompassModel(-panoMeasurements[0].compass + 90 || 0);
  } else {
    compass = new CompassModel(0);
  }

  type LayoutItem = {
    index: number;
    pano: PanoMeasurementModel;
    panoDescription: string;
    walls: any[];
    boundaryPoints: THREE.Vector3[];
    roomAttributes: any[];
    floorAttributes: any[];
    cameraOffset: THREE.Vector3;
    minX: number;
    minY: number;
    width: number;
    height: number;
    footW: number;
    footH: number;
    isSoloRow: boolean;
    isLegacy: boolean;
  };

  const items: LayoutItem[] = await Promise.all(
    panoMeasurements.map(async (pano, index) => {
      let walls: Array<any> = [];
      let boundaryPoints: THREE.Vector3[] = [];
      let cameraOffset: THREE.Vector3 = new THREE.Vector3();
      let panoDescription: string = '';
      let roomAttributes: any[] = [];
      let floorAttributes: any[] = [];
      let isLegacy = false;
      let compassValue: number | undefined = typeof pano.compass === 'number' ? pano.compass : Number(pano.compass);
      let alignmentRotationDegrees = 0;

      if (pano.measurements.length === 1 && 'version' in pano.measurements[0]) {
        const sceneExport = pano.measurements[0] as any;
        const wallImport = importWalls(sceneExport) as ImportedWallsResult;

        compassValue = getVersionedCompassValue(pano, sceneExport);

        walls = wallImport.walls;
        cameraOffset = wallImport.cameraOffset ?? new THREE.Vector3();
        panoDescription = sceneExport.description;
        roomAttributes = wallImport.roomAttributes ?? [];
        floorAttributes = wallImport.floorAttributes ?? [];
        boundaryPoints = (wallImport.boundaryPoints ?? []).map((p) => p.clone());

        if (isSingleImport) {
          const alignedImport = alignImportedRoomToLongestWall(walls, boundaryPoints, cameraOffset);
          walls = alignedImport.walls;
          boundaryPoints = alignedImport.boundaryPoints;
          cameraOffset = alignedImport.cameraOffset;
          singleImportAlignmentRotationDegrees = alignedImport.alignmentRotationDegrees;
        }
      } else {
        const importObject = createWallsFromMeasurements(pano);
        walls = importObject.walls;
        boundaryPoints = importObject.boundaryPoints.map((p) => p.clone());
        cameraOffset = importObject.cameraOffset;
        panoDescription = pano.description;
        isLegacy = true;
        alignmentRotationDegrees = importObject.alignmentRotationDegrees;

        if (isSingleImport) {
          singleImportAlignmentRotationDegrees = importObject.alignmentRotationDegrees;
        }
      }

      if (
        !isSingleImport &&
        typeof compassValue === 'number' &&
        Number.isFinite(compassValue) &&
        compassValue !== 0
      ) {
        const finalRotationDegrees = -compassValue + 90 + alignmentRotationDegrees;
        const normalizedFinalRotationDegrees = ((finalRotationDegrees % 360) + 360) % 360;

        const rotateAngle = normalizedFinalRotationDegrees * (Math.PI / 180);
        const zAxis = new THREE.Vector3(0, 0, 1);
        const rotateVec = (v: THREE.Vector3) => v.clone().applyAxisAngle(zAxis, rotateAngle);

        walls = walls.map((w: any) => {
          if (w.startPoint) w.startPoint = rotateVec(w.startPoint);
          if (w.endPoint) w.endPoint = rotateVec(w.endPoint);

          if (Array.isArray(w.windows)) {
            w.windows.forEach((win: any) => {
              if (win.position) win.position = rotateVec(win.position);
              if (typeof win.angle === 'number') win.angle += rotateAngle;
            });
          }
          if (Array.isArray(w.doors)) {
            w.doors.forEach((door: any) => {
              if (door.position) door.position = rotateVec(door.position);
              if (typeof door.angle === 'number') door.angle += rotateAngle;
            });
          }
          if (typeof w.getAllRadiators === 'function') {
            w.getAllRadiators().forEach((rad: any) => {
              if (rad.position) rad.position = rotateVec(rad.position);
              if (typeof rad.angle === 'number') rad.angle += rotateAngle;
            });
          }

          if (typeof w.resetPoints === 'function') w.resetPoints();
          return w;
        });

        boundaryPoints = boundaryPoints.map((p) => rotateVec(p));
        if (cameraOffset) cameraOffset = rotateVec(cameraOffset);
      }

      const bounds = boundaryPoints.length >= 3 ? getRoomBoundsWithMinFromPoints(boundaryPoints) : getRoomBoundsWithMin(walls);

      const footW = bounds.width + PAD * 2;
      const footH = bounds.height + PAD * 2;

      const widthM = bounds.width / WORLD_UNITS_PER_METER;
      const heightM = bounds.height / WORLD_UNITS_PER_METER;
      const isSoloRow = Math.max(widthM, heightM) > SOLO_ROW_THRESHOLD_M;

      return {
        index,
        pano,
        panoDescription,
        walls,
        boundaryPoints,
        roomAttributes,
        floorAttributes,
        cameraOffset,
        minX: bounds.minX,
        minY: bounds.minY,
        width: bounds.width,
        height: bounds.height,
        footW,
        footH,
        isSoloRow,
        isLegacy,
      };
    }),
  );

  if (isSingleImport) {
    const baseCompass = -panoMeasurements[0].compass + 90 || 0;
    compass = new CompassModel(baseCompass + singleImportAlignmentRotationDegrees);
  }

  const soloItems = items.filter((i) => i.isSoloRow).sort((a, b) => a.index - b.index);
  const gridItems = items.filter((i) => !i.isSoloRow).sort((a, b) => a.index - b.index);

  const finalOffsets = new Map<number, THREE.Vector3>();

  let yCursor = 0;
  for (const item of soloItems) {
    let xPos = 0;
    if (xPos + item.footW > RIGHT_LIMIT) xPos = RIGHT_LIMIT - item.footW;
    if (xPos < LEFT_LIMIT) xPos = LEFT_LIMIT;

    finalOffsets.set(item.index, new THREE.Vector3(-item.minX + PAD + xPos, -item.minY + PAD - yCursor, 0));
    yCursor += item.footH + ROOM_SPACING;
  }

  if (gridItems.length > 0) {
    const tryColsStart = Math.max(1, Math.ceil(Math.sqrt(gridItems.length)));

    const rowWidthForCols = (cols: number) => {
      const rows = Math.ceil(gridItems.length / cols);
      let maxRowWidth = 0;
      for (let r = 0; r < rows; r++) {
        const row = gridItems.slice(r * cols, Math.min(r * cols + cols, gridItems.length));
        const w = row.reduce((acc, it) => acc + it.footW, 0) + (row.length - 1) * ROOM_SPACING;
        maxRowWidth = Math.max(maxRowWidth, w);
      }
      return maxRowWidth;
    };

    let cols = tryColsStart;
    while (cols > 1 && rowWidthForCols(cols) > RIGHT_LIMIT) cols--;

    const rows = Math.ceil(gridItems.length / cols);
    const rowHeights: number[] = new Array(rows).fill(0);
    for (let i = 0; i < gridItems.length; i++) {
      rowHeights[Math.floor(i / cols)] = Math.max(rowHeights[Math.floor(i / cols)], gridItems[i].footH);
    }

    const rowOffsets: number[] = [0];
    for (let r = 1; r < rows; r++) {
      rowOffsets[r] = rowOffsets[r - 1] + rowHeights[r - 1] + ROOM_SPACING;
    }

    for (let r = 0; r < rows; r++) {
      const row = gridItems.slice(r * cols, Math.min(r * cols + cols, gridItems.length));
      const rowWidth = row.reduce((acc, it) => acc + it.footW, 0) + (row.length - 1) * ROOM_SPACING;

      let rowStartX = 0;
      if (rowStartX + rowWidth > RIGHT_LIMIT) rowStartX = RIGHT_LIMIT - rowWidth;
      if (rowStartX < LEFT_LIMIT) rowStartX = LEFT_LIMIT;

      let x = rowStartX;
      const yPos = yCursor + rowOffsets[r];

      for (const item of row) {
        finalOffsets.set(item.index, new THREE.Vector3(-item.minX + PAD + x, -item.minY + PAD - yPos, 0));
        x += item.footW + ROOM_SPACING;
      }
    }
  }

  let gMinX = Infinity,
    gMaxX = -Infinity;
  let gMinY = Infinity,
    gMaxY = -Infinity;

  for (const item of items) {
    const off = finalOffsets.get(item.index) || new THREE.Vector3();
    gMinX = Math.min(gMinX, item.minX + off.x - PAD);
    gMaxX = Math.max(gMaxX, item.minX + item.width + off.x + PAD);
    gMinY = Math.min(gMinY, item.minY + off.y - PAD);
    gMaxY = Math.max(gMaxY, item.minY + item.height + off.y + PAD);
  }

  let shiftX = 0;
  let shiftY = 0;

  if (existingBuilding && existingBuilding.walls && existingBuilding.walls.length > 0) {
    const existingBBox = getBoundingBox(existingBuilding);
    const newImportWidth = gMaxX - gMinX;

    const potentialRightX = existingBBox.maxX + ROOM_SPACING * 3;
    const wouldExceedRight = potentialRightX + newImportWidth > RIGHT_LIMIT;

    if (wouldExceedRight) {
      shiftX = existingBBox.minX - gMinX;
      shiftY = existingBBox.minY - ROOM_SPACING * 3 - gMaxY;
    } else {
      shiftX = potentialRightX - gMinX;
      shiftY = existingBBox.maxY - gMaxY;
    }
  } else {
    shiftX = -(gMinX + gMaxX) / 2;
    shiftY = -(gMinY + gMaxY) / 2;
  }

  const globalShift = new THREE.Vector3(shiftX, shiftY, 0);

  const legacyRooms: any[] = [];
  const v1Rooms: any[] = [];
  const legacyWalls: any[] = [];
  const v1Walls: any[] = [];

  for (const item of items) {
    const offset = (finalOffsets.get(item.index) || new THREE.Vector3()).clone().add(globalShift);

    const translatedWalls = item.walls.map((w: any) => {
      const s = w.startPoint.clone().add(offset);
      const e = w.endPoint.clone().add(offset);

      const transformedWindows = w.windows.map((window: any) => {
        const scrubbedAttrs = (window.attributes as any[]).map((attr) => {
          if (attr.id === DefaultAttributeId.WINDOW_AREA) {
            return new AttributeModel(
              attr.id, attr.key, undefined, attr.predecessorId, attr.type, attr.unit, attr.hidden, attr.forced, attr.ownerId, attr.userId, attr.publicFlag, attr.localization, attr.defaults, attr.validation, attr.unitFamily,
            );
          }
          return attr;
        });
        return new WindowModel(window.position.clone().add(offset), window.length, window.depth, window.angle, scrubbedAttrs, window.uuid);
      });

      const transformedRadiators = w.getAllRadiators().map((radiator: any) => {
        const scrubbedAttrs = (radiator.attributes as any[]).map((attr) => {
          if (attr.id === DefaultAttributeId.RADIATOR_AREA) {
            return new AttributeModel(
              attr.id, attr.key, undefined, attr.predecessorId, attr.type, attr.unit, attr.hidden, attr.forced, attr.ownerId, attr.userId, attr.publicFlag, attr.localization, attr.defaults, attr.validation, attr.unitFamily,
            );
          }
          return attr;
        });
        return new RadiatorModel(radiator.position.clone().add(offset), radiator.length, radiator.depth, radiator.angle, scrubbedAttrs, radiator.uuid);
      });

      const transformedDoors = w.doors.map((door: any) => {
        const scrubbedAttrs = (door.attributes as any[]).map((attr) => {
          if (attr.id === DefaultAttributeId.DOOR_AREA) {
            return new AttributeModel(
              attr.id, attr.key, undefined, attr.predecessorId, attr.type, attr.unit, attr.hidden, attr.forced, attr.ownerId, attr.userId, attr.publicFlag, attr.localization, attr.defaults, attr.validation, attr.unitFamily,
            );
          }
          return attr;
        });
        return new DoorModel(door.position.clone().add(offset), door.length, door.depth, door.angle, door.openingDirection, door.orientation, scrubbedAttrs, door.uuid);
      });

      const transformedPassages = (w.passages ?? []).map((passage: any) => {
        const scrubbedAttrs = ((passage.attributes ?? []) as any[]).map((attr) => attr);
        return new PassageModel(passage.position.clone().add(offset), passage.length, passage.depth, passage.angle, scrubbedAttrs, passage.uuid);
      });

      const filteredAttributes = w.attributes.map((attr: any) => {
        if (
          attr.id === DefaultAttributeId.WALL_AREA ||
          attr.id === DefaultAttributeId.GROSS_WALL_AREA ||
          attr.id === DefaultAttributeId.NET_WALL_AREA
        ) {
          return new AttributeModel(
            attr.id, attr.key, undefined, attr.predecessorId, attr.type, attr.unit, attr.hidden, attr.forced, attr.ownerId, attr.userId, attr.publicFlag, attr.localization, attr.defaults, attr.validation, attr.unitFamily,
          );
        }
        return attr;
      });

      const wall: any =
        w instanceof StandardWallModel || w.type === EWall.STANDARD
          ? new StandardWallModel(s, e, w.depth, transformedWindows, transformedDoors, transformedRadiators, filteredAttributes, w.uuid, transformedPassages)
          : new PartitionWallModel(s, e, w.depth, transformedWindows, transformedDoors, transformedRadiators, filteredAttributes, w.uuid, transformedPassages);

      if (wall.depth === 0) wall.depth = DEFAULT_WALL_DEPTH;
      return wall;
    });

    const translatedStandardWalls = translatedWalls.filter((w: any) => w instanceof StandardWallModel || w.type === EWall.STANDARD);

    calculatedCameraPositions[item.index] = item.cameraOffset.clone().add(offset);

    const boundary = new PhysicalBoundaryModel(translatedStandardWalls);

    // Übergabe der fertig konvertierten Center-Line-Standardwände für die korrekte Boundary
    const boundaryEdges = translatedStandardWalls.map((w: any) => new BoundaryEdge(w, 0, 1, 'A', false));
    boundary.setEdges(boundaryEdges);

    const floor = new FloorModel(undefined, null, item.floorAttributes);
    const space = new SpaceModel(floor, [], [], [], [boundary], translatedWalls);

    const filteredRoomAttributes = item.roomAttributes.map((attr: any) => {
      if (
        attr.id === DefaultAttributeId.WINDOW_AREA ||
        attr.id === DefaultAttributeId.RADIATOR_AREA ||
        attr.id === DefaultAttributeId.DOOR_AREA ||
        attr.id === DefaultAttributeId.WALL_AREA ||
        attr.id === DefaultAttributeId.GROSS_WALL_AREA ||
        attr.id === DefaultAttributeId.NET_WALL_AREA
      ) {
        return new AttributeModel(
          attr.id, attr.key, undefined, attr.predecessorId, attr.type, attr.unit, attr.hidden, attr.forced, attr.ownerId, attr.userId, attr.publicFlag, attr.localization, attr.defaults, attr.validation, attr.unitFamily,
        );
      }
      return attr;
    });

    const room = new RoomModel(item.panoDescription ?? `Room ${item.index + 1}`, [space], filteredRoomAttributes);

    if (item.isLegacy) {
      legacyRooms.push(room);
      legacyWalls.push(...translatedWalls);
    } else {
      v1Rooms.push(room);
      v1Walls.push(...translatedWalls);
    }
  }

  let rawBuilding: IBuildingProviderContext = {
    compass,
    rooms: legacyRooms,
    walls: legacyWalls,
    products: [],
    elements: [],
    models2D: [],
    nonFetchableEntities: [],
    relations: [],
    isSetByUndoRedo: false,
    lastAction: EHistoryAction.ROOM_CREATED,
  };

  if (legacyRooms.length > 0) {
    rawBuilding = await assignAttributesToImportedPanoMeasurements(rawBuilding, region);
  }

  rawBuilding.rooms = [...v1Rooms, ...rawBuilding.rooms];
  rawBuilding.walls = [...v1Walls, ...rawBuilding.walls];

  if (existingBuilding) {
    rawBuilding.rooms = [...(existingBuilding.rooms || []), ...rawBuilding.rooms];
    rawBuilding.walls = [...(existingBuilding.walls || []), ...rawBuilding.walls];
    rawBuilding.products = [...(existingBuilding.products || []), ...rawBuilding.products];
    rawBuilding.elements = [...(existingBuilding.elements || []), ...rawBuilding.elements];
    rawBuilding.models2D = [...(existingBuilding.models2D || []), ...rawBuilding.models2D];
    rawBuilding.nonFetchableEntities = [...(existingBuilding.nonFetchableEntities || []), ...rawBuilding.nonFetchableEntities];
    rawBuilding.relations = [...(existingBuilding.relations || []), ...rawBuilding.relations];
  }

  return {
    building: rawBuilding,
    cameraOffsets: calculatedCameraPositions,
  };
};
